import {
  createContext, FC, ReactNode, useContext, useEffect, useState,
} from 'react';
import { toast } from 'react-toastify';

import { LocationContext } from 'context/LocationContext';

import { useKiosk } from 'hooks/useKiosk';

import { Bounty } from 'types/bounty.interface';
import { Cart } from 'types/cart.interface';
import { ProductSpecificInfo } from 'types/product.interface';
import { formatProductForCart, getStoreCartKey } from 'utils/cartUtils';
import { getProductEquivalentQuantity, getRealQuantity } from 'utils/productUtils';
import { deleteCartFromStorage, getCartFromStorage, saveCartToStorage } from 'utils/storageUtils';
import { reportAlert } from 'utils/utils';

interface AddItem {
  bounty: Bounty;
  quantity: number;
  productVariant: ProductSpecificInfo;
  onSuccess: (cart: Cart) => void;
}

interface AddItemWithWeight {
  bounty: Bounty;
  weight: string;
  productVariant: ProductSpecificInfo;
  onSuccess: (cart: Cart) => void;
}

const CartContext = createContext({
  products: {},
  kioskLabel: 'CHECKOUT',
} as Cart & { kioskLabel: string });

const CartDispatchContext = createContext({
  removeItem: (productId: string) => {}, // eslint-disable-line
  addItem: ({ bounty, quantity, productVariant, onSuccess }: AddItem ) => {}, // eslint-disable-line
  addItemWithWeight: ({ bounty, weight, productVariant, onSuccess }: AddItemWithWeight) => {}, // eslint-disable-line
  setQuantity: (productId: string, quantity: number, onSuccess?: (cart: Cart) => void) => {}, // eslint-disable-line
  setWeight: (productId: string, weight: string, productWeight: number) => {}, // eslint-disable-line
  removeItems: () => {}, // eslint-disable-line
  updateKioskLabel: (label: string) => {}, // eslint-disable-line
});

interface CartProviderProps {
  children: ReactNode;
}

const CartProvider:FC<CartProviderProps> = ({ children }) => {
  const [cartDetails, setCartDetails] = useState<Cart>({ products: {} } as Cart);
  const storeKey = getStoreCartKey();
  const { onboardingInfo } = useContext(LocationContext);
  const { kioskMode } = useKiosk();
  const [kioskLabel, setKioskLabel] = useState('CHECKOUT');

  useEffect(() => {
    if (onboardingInfo?.storeId && onboardingInfo?.useType) {
      const savedCart = getCartFromStorage() || {};
      const storeCart = savedCart?.[storeKey] || { products: {} } as Cart;

      if (!savedCart?.[storeKey]) {
        saveCartToStorage({ ...savedCart, [storeKey]: storeCart });
      }
      setCartDetails(storeCart);
    }
  }, [onboardingInfo?.storeId, onboardingInfo?.useType]);

  const saveCart = (cart:Cart): void => {
    const savedCart = getCartFromStorage();

    setCartDetails(cart);
    saveCartToStorage({ ...savedCart, [storeKey]: cart });
  };

  const removeItem = (productId: string): void => {
    const cart = JSON.parse(JSON.stringify(cartDetails));
    delete cart?.products?.[productId];
    saveCart(cart);
  };

  const addItem = ({
    bounty, quantity: quantityToAdd, onSuccess, productVariant,
  }: AddItem) => {
    const { quantity, maxAllowedQty } = productVariant;
    const productQuantity = Math.max(0, quantity || 0);
    const availableQuantity = getRealQuantity(quantity, bounty.id, cartDetails);

    if (productQuantity <= 0) {
      toast.info('Product is out of stock');
      return;
    }

    if (availableQuantity <= 0) {
      toast.info('Maximum available quantity for this item has already been added to cart');
      return;
    }

    if (!bounty?.id) {
      reportAlert(`Cannot add product to cart, missing id:\n${JSON.stringify(bounty)}`);
      toast.info('The product is missing important information, we can\'t add it to the cart.');
      return;
    }

    const existingBounty = cartDetails?.products?.[`temp:${bounty.id}`];
    const newQuantity = existingBounty?.quantity ? quantityToAdd + existingBounty.quantity : quantityToAdd;
    const formattedProduct = formatProductForCart(bounty, newQuantity, productVariant, null, kioskMode);

    if (maxAllowedQty && newQuantity > maxAllowedQty) {
      toast.info('Maximum available quantity for this item has already been added to cart');
      return;
    }

    const updatedCart = {
      ...cartDetails,
      products: {
        ...cartDetails?.products,
        [formattedProduct.id]: formattedProduct,
      },
    };

    saveCart(updatedCart);
    onSuccess(updatedCart);
  };

  const addItemWithWeight = ({
    bounty, weight, productVariant, onSuccess,
  }: AddItemWithWeight) => {
    const { quantity, maxAllowedQty } = productVariant;
    const productQuantity = Math.max(0, quantity || 0);
    const availableQuantity = getRealQuantity(quantity, bounty.id, cartDetails);

    if (!productVariant?.sortWeight) {
      reportAlert(`Cannot add product to cart, missing sortWeight:\n${JSON.stringify(bounty)}`);
      toast.info('The product is missing important information, we can\'t add it to the cart.');
      return;
    }

    const equivalentQuantity = getProductEquivalentQuantity(weight, productVariant?.sortWeight, kioskMode);

    if (equivalentQuantity > availableQuantity) {
      toast.info('The weight limit was exceeded. Please lower the weight.');
      return;
    }

    if (productQuantity <= 0) {
      toast.info('Product is out of stock.');
      return;
    }

    if (availableQuantity <= 0) {
      toast.info('Maximum available quantity for this item has already been added to cart.');
      return;
    }

    if (!bounty?.id) {
      reportAlert(`Cannot add product to cart, missing id:\n${JSON.stringify(bounty)}`);
      toast.info('The product is missing important information, we can\'t add it to the cart.');
      return;
    }

    const existingProduct = cartDetails?.products?.[`temp:${bounty.id}`];
    const newWeight = existingProduct?.weight
      ? (+weight + +existingProduct.weight).toString()
      : weight;
    const newEquivalentQuantity = getProductEquivalentQuantity(newWeight, productVariant?.sortWeight, kioskMode);
    const formattedProduct = formatProductForCart(bounty, newEquivalentQuantity, productVariant, newWeight, kioskMode);

    if (maxAllowedQty && newEquivalentQuantity > maxAllowedQty) {
      toast.info('Maximum available quantity for this item has already been added to cart');
      return;
    }

    const updatedCart = {
      ...cartDetails,
      products: {
        ...cartDetails?.products,
        [formattedProduct.id]: formattedProduct,
      },
    };

    saveCart(updatedCart);
    onSuccess(updatedCart);
  };

  const setQuantity = (productId: string, quantity: number, onSuccess?: (cart:Cart) => void): void => {
    const updatedCart = {
      ...cartDetails,
      products: {
        ...cartDetails?.products,
        [productId]: {
          ...cartDetails?.products[productId],
          quantity,
        },
      },
    };

    saveCart(updatedCart);

    if (onSuccess) {
      onSuccess(updatedCart);
    }
  };

  const setWeight = (productId: string, weight: string, productWeight: number): void => {
    const equivalentQuantity = getProductEquivalentQuantity(weight, productWeight, kioskMode);

    saveCart({
      ...cartDetails,
      products: {
        ...cartDetails?.products,
        [productId]: {
          ...cartDetails?.products[productId],
          weight,
          quantity: equivalentQuantity,
        },
      },
    });
  };

  const removeItems = (): void => {
    deleteCartFromStorage();
    saveCart({ products: {} } as Cart);
  };

  const updateKioskLabel = (label: string) => {
    setKioskLabel(label);
    setTimeout(() => setKioskLabel('CHECKOUT'), 2000);
  };

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <CartContext.Provider value={{ ...cartDetails, kioskLabel }}>
      {/* eslint-disable-next-line react/jsx-no-constructed-context-values */}
      <CartDispatchContext.Provider value={{
        removeItem,
        addItem,
        addItemWithWeight,
        setQuantity,
        setWeight,
        removeItems,
        updateKioskLabel,
      }}
      >
        {children}
      </CartDispatchContext.Provider>
    </CartContext.Provider>
  );
};

export {
  CartProvider,
  CartContext,
  CartDispatchContext,
};
